import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash2, Save, RotateCcw, ExternalLink } from 'lucide-react';
import { adminApi } from '../lib/adminApi';
import { CATEGORIES } from '../lib/constants';

const PAGE_SLOTS = [
  {
    key: 'about_main',
    label: 'About Us — Main Image',
    page: '/about',
    fallback: 'https://images.pexels.com/photos/11742213/pexels-photo-11742213.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900',
    aspect: '4 / 5',
    note: 'Shown on the right side of the About page (portrait, 4:5).',
  },
  {
    key: 'careers_main',
    label: 'Careers — Main Image',
    page: '/careers',
    fallback: 'https://images.pexels.com/photos/13802704/pexels-photo-13802704.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900',
    aspect: '4 / 5',
    note: 'Shown on the right side of the Careers page (portrait, 4:5).',
  },
  {
    key: 'journal_main',
    label: 'Journal — Main Image',
    page: '/blog',
    fallback: 'https://images.pexels.com/photos/13802704/pexels-photo-13802704.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900',
    aspect: '4 / 5',
    note: 'Shown on the right side of the Journal page (portrait, 4:5).',
  },
];

const HOME_SLOTS = [
  {
    key: 'for_artists_main',
    label: 'Home — For Artists Section Image',
    page: '/#mua-cta-section',
    fallback: 'https://images.unsplash.com/photo-1644134395029-d68dd6b97761?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400',
    aspect: '4 / 5',
    note: 'Shown on the right side of the pink "For Artists / Get discovered" section on the Home page.',
  },
];

const CATEGORY_SLOTS = CATEGORIES.map((c) => ({
  key: `category_${c.key.replace(/-/g, '_')}`,
  label: `${c.title} — Background`,
  page: `/artists?occasions=${encodeURIComponent(c.title)}`,
  fallback: c.image,
  aspect: '4 / 5',
  note: `Background image for the "${c.title}" tile in the Browse By Style section on the Home page.`,
}));

const SLOT_GROUPS = [
  { title: 'Pages', subtitle: 'Main image on standalone pages.', items: PAGE_SLOTS },
  { title: 'Home — Sections', subtitle: 'Images embedded in specific sections on the Home page.', items: HOME_SLOTS },
  { title: 'Home — Browse By Style', subtitle: 'Background images for the 7 category tiles on the Home page.', items: CATEGORY_SLOTS },
];

const ImageSlot = ({ slot, overrides, drafts, busy, onChange, onSave, onReset }) => {
  const current = overrides[slot.key];
  const draft = drafts[slot.key] ?? '';
  const previewSrc = (draft && draft.trim()) || current || slot.fallback;
  const isOverridden = Boolean(current);
  const saving = busy[slot.key] === 'save';
  const resetting = busy[slot.key] === 'reset';

  return (
    <div
      data-testid={`site-image-row-${slot.key}`}
      className="bg-white border border-[#eaeaea] p-5 md:p-6 grid md:grid-cols-12 gap-5 md:gap-8"
    >
      <div className="md:col-span-4">
        <div
          className="bg-[#f7f7f7] overflow-hidden w-full"
          style={{ aspectRatio: slot.aspect }}
        >
          <img
            src={previewSrc}
            alt={slot.label}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.src = slot.fallback; }}
          />
        </div>
      </div>

      <div className="md:col-span-8 flex flex-col">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="font-serif text-xl md:text-2xl">{slot.label}</div>
            <div className="mt-1 text-xs text-neutral-500">{slot.note}</div>
          </div>
          <a
            href={slot.page}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[#eb5381] hover:text-[#d64570]"
          >
            Preview <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        <div className="mt-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em]">
          <span className={isOverridden ? 'text-[#eb5381]' : 'text-neutral-400'}>
            {isOverridden ? 'Custom image active' : 'Using default image'}
          </span>
        </div>

        <label className="mt-4 block text-[10px] uppercase tracking-[0.25em] text-neutral-500">Image URL</label>
        <input
          type="url"
          value={draft}
          data-testid={`site-image-input-${slot.key}`}
          placeholder={current || slot.fallback}
          onChange={(e) => onChange(slot.key, e.target.value)}
          className="mt-2 w-full bg-white border border-[#eaeaea] focus:border-[#eb5381] outline-none px-3 py-2.5 text-sm"
        />

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={saving || !draft.trim() || draft.trim() === current}
            onClick={() => onSave(slot)}
            data-testid={`site-image-save-${slot.key}`}
            className="inline-flex items-center gap-2 bg-[#eb5381] hover:bg-[#d64570] disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2.5 text-[10px] uppercase tracking-[0.25em] transition-colors"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save'}
          </button>

          <button
            type="button"
            disabled={resetting || (!isOverridden && !draft)}
            onClick={() => onReset(slot)}
            data-testid={`site-image-reset-${slot.key}`}
            className="inline-flex items-center gap-2 border border-neutral-300 hover:border-black hover:bg-black hover:text-white disabled:opacity-50 disabled:cursor-not-allowed px-5 py-2.5 text-[10px] uppercase tracking-[0.25em] transition-colors"
          >
            {isOverridden
              ? <><Trash2 className="w-3.5 h-3.5" /> {resetting ? 'Resetting…' : 'Reset to default'}</>
              : <><RotateCcw className="w-3.5 h-3.5" /> Clear field</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AdminSiteImages() {
  const [overrides, setOverrides] = useState({});
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.listSiteImages()
      .then(data => {
        setOverrides(data || {});
        setDrafts(data || {});
      })
      .catch(() => toast.error('Could not load saved images'))
      .finally(() => setLoading(false));
  }, []);

  const onChange = (key, value) => setDrafts(d => ({ ...d, [key]: value }));

  const onSave = async (slot) => {
    const url = (drafts[slot.key] || '').trim();
    if (!url) { toast.error('Paste an image URL first'); return; }
    setBusy(b => ({ ...b, [slot.key]: 'save' }));
    try {
      await adminApi.setSiteImage(slot.key, url);
      setOverrides(o => ({ ...o, [slot.key]: url }));
      toast.success(`${slot.label} updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setBusy(b => ({ ...b, [slot.key]: null }));
    }
  };

  const onReset = async (slot) => {
    if (!overrides[slot.key]) {
      setDrafts(d => ({ ...d, [slot.key]: '' }));
      return;
    }
    setBusy(b => ({ ...b, [slot.key]: 'reset' }));
    try {
      await adminApi.removeSiteImage(slot.key);
      setOverrides(o => { const c = { ...o }; delete c[slot.key]; return c; });
      setDrafts(d => ({ ...d, [slot.key]: '' }));
      toast.success(`${slot.label} reset to default`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reset');
    } finally {
      setBusy(b => ({ ...b, [slot.key]: null }));
    }
  };

  if (loading) {
    return <div className="text-sm uppercase tracking-[0.25em] text-neutral-500">Loading site images…</div>;
  }

  return (
    <div data-testid="admin-site-images-page" className="max-w-5xl">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">Manage</div>
        <h1 className="mt-1 font-serif text-3xl md:text-4xl">Site Images</h1>
        <p className="mt-3 text-sm text-neutral-600 max-w-2xl leading-relaxed">
          Replace any imagery shown across the site without touching code. Paste a public image URL
          (e.g. from Unsplash, Pexels, ImageKit, Cloudinary, or a Google Drive direct-view link).
          Use the <span className="font-medium">Reset</span> button to revert to the original default.
        </p>
        <p className="mt-2 text-xs text-neutral-500">
          Tip: For Google Drive, share as "Anyone with the link" and use the direct format
          {' '}<code className="bg-[#f7f7f7] px-1">https://drive.google.com/uc?export=view&amp;id=FILE_ID</code>.
        </p>
      </div>

      {SLOT_GROUPS.map(group => (
        <section key={group.title} className="mb-12">
          <div className="mb-5 pb-3 border-b border-[#eaeaea]">
            <h2 className="font-serif text-2xl">{group.title}</h2>
            <p className="mt-1 text-xs text-neutral-500">{group.subtitle}</p>
          </div>
          <div className="space-y-6">
            {group.items.map(slot => (
              <ImageSlot
                key={slot.key}
                slot={slot}
                overrides={overrides}
                drafts={drafts}
                busy={busy}
                onChange={onChange}
                onSave={onSave}
                onReset={onReset}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
